/**
 * Clipboard service for handling copy operations
 */

export const copyImageToClipboard = async (imageUrl: string): Promise<boolean> => {
  try {
    // Check if Clipboard API is supported
    if (!navigator.clipboard || !navigator.clipboard.write) {
      console.warn('Clipboard API not supported');
      return false;
    }

    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }

    const blob = await response.blob();
    
    // Check if the blob type is supported for clipboard
    const supportedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!supportedTypes.includes(blob.type)) {
      console.warn(`Unsupported image type for clipboard: ${blob.type}`);
      return false;
    }

    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob
      })
    ]);
    
    return true;
  } catch (error) {
    console.error('Failed to copy image:', error);
    return false;
  }
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    // Check if Clipboard API is supported
    if (!navigator.clipboard || !navigator.clipboard.writeText) {
      // Fallback for older browsers
      return fallbackCopyText(text);
    }

    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy text:', error);
    // Try fallback method
    return fallbackCopyText(text);
  }
};

/**
 * Fallback method for copying text in older browsers
 */
const fallbackCopyText = (text: string): boolean => {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (error) {
    console.error('Fallback copy failed:', error);
    return false;
  }
};

/**
 * Check if clipboard operations are supported
 */
export const isClipboardSupported = (): boolean => {
  return !!(navigator.clipboard && navigator.clipboard.writeText);
};

/**
 * Check if image clipboard operations are supported
 */
export const isImageClipboardSupported = (): boolean => {
  return !!(navigator.clipboard && navigator.clipboard.write);
}; 